/**
 * ReportsChipView — paperclip icon + report count chip, folding in the avg
 * scout rating when present ("3.2 · 4 reports", SE-4143 behavior). Promoted
 * verbatim from MobileTargetCard's inline chip (SE-5527) — classes unchanged
 * so the card's layout and SSIM baselines are unaffected. Hides entirely
 * when there is no rating and no reports.
 * @module components/molecules/ReportsChip/ReportsChipView
 */
import { Paperclip } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Reports chip label — folds the avg rating in when present.
 * @param avgScoutRating - Overall avg scout rating, or null
 * @param count - Report count
 * @returns Chip label (e.g. "3.2 · 4 reports" or "3 reports")
 */
const reportsLabel = (avgScoutRating: number | null, count: number): string => {
  const reports = `${count} report${count === 1 ? "" : "s"}`;
  return avgScoutRating != null
    ? `${avgScoutRating.toFixed(1)} · ${reports}`
    : reports;
};

/** Props for ReportsChipView. */
export interface ReportsChipProps {
  /** Overall avg scout rating — prefixes the count when present. */
  readonly avgScoutRating: number | null;
  /** Number of scout reports. */
  readonly scoutReportCount: number | null;
  /** Optional test identity (call sites decide when to apply it). */
  readonly testID?: string;
}

/**
 * Renders the reports chip, or nothing when there is no data.
 * @param root0 - Reports data
 * @param root0.avgScoutRating - Overall avg scout rating, or null
 * @param root0.scoutReportCount - Report count
 * @param root0.testID - Optional test identity
 * @returns The chip element, or null.
 */
const ReportsChipView = ({
  avgScoutRating,
  scoutReportCount,
  testID,
}: ReportsChipProps) =>
  avgScoutRating == null && (scoutReportCount ?? 0) <= 0 ? null : (
    <Stack
      testID={testID}
      direction="horizontal"
      className="shrink-0 items-center gap-1 rounded-full border border-outline-subtle bg-surface-raised px-2 py-1"
    >
      <Icon as={Paperclip} size="2xs" className="text-content-muted" />
      <Text variant="micro" className="text-content-primary">
        {reportsLabel(avgScoutRating, scoutReportCount ?? 0)}
      </Text>
    </Stack>
  );

ReportsChipView.displayName = "ReportsChipView";

export default memo(ReportsChipView);
